import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { useState } from 'react'

import { IllustrationName } from '@/components/icons/Illustration'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CacheKeys, invalidateApplicationSummary } from '../../lib/cache'
import { cn } from '../../lib/utils'
import { addTruck, editTruck, type Truck, useTrucks } from '../../models/equipment'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { isFleetReadonly, useCurrentUser } from '../../models/user'
import { EditEquipmentModal } from '../EquipmentTable'
import { truckConfig } from '../TrucksTable'
import { IllustratedRow } from './ConfirmInfoCard'

export interface ChatOwnerOperatorTruckProps {
  /** The one truck on file (CAB's, usually), edited in place; without it the modal starts blank. */
  truckId: string | null
  orgId: string | undefined
  handleSubmit?: (data: ChatActionWithValue | FormData) => void
  readonly?: boolean
}

/** One driver, one truck: the trucks step as the Add Truck modal alone, opened on arrival. */
export function ChatOwnerOperatorTruck({
  truckId,
  orgId,
  handleSubmit,
  readonly,
}: ChatOwnerOperatorTruckProps) {
  const queryClient = useQueryClient()
  const { trucks, isLoading, refetch } = useTrucks({ orgId, shouldFetchAllPages: true })
  const { user } = useCurrentUser({ enabled: false })
  const isDisabled = readonly || isFleetReadonly(user)
  const [isOpen, setIsOpen] = useState(!isDisabled)
  const [newTruck] = useState(() => truckConfig.createEmpty() as Truck)

  const existing = truckId ? trucks.find((truck) => truck.id === truckId) : undefined
  const truck = existing ?? newTruck
  // A truck on file that didn't load must never open as a blank form, or saving would add a second one.
  const failedToLoad = !isLoading && !!truckId && !existing

  async function save(entered: Truck) {
    if (!orgId) return
    if (existing) await editTruck(orgId, existing.id, entered)
    else await addTruck(orgId, entered)
    queryClient.invalidateQueries({ queryKey: [CacheKeys.TRUCKS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
    setIsOpen(false)
    handleSubmit?.({
      content: {
        component: 'ADD_EQUIPMENTS',
        content: ``,
        props: { disableEnteringAnimation: true, edit: true, type: 'trucks' },
      },
      value: { name: 'ADD_EQUIPMENTS', type: 'trucks' },
    })
  }

  return (
    <>
      <div
        className={cn(
          'mt-6 flex w-full cursor-pointer items-center justify-end gap-2 font-semibold',
          isDisabled && 'cursor-auto opacity-50'
        )}
        onClick={isDisabled ? undefined : () => setIsOpen(true)}
      >
        <Card className="w-full bg-transparent">
          <CardContent>
            <IllustratedRow
              illustration={IllustrationName.TRUCK}
              label={
                failedToLoad
                  ? 'Couldn’t load your truck'
                  : isLoading
                    ? 'Loading trucks...'
                    : `${trucks.length} active ${pluralize('truck', trucks.length)}`
              }
              action={
                !isDisabled &&
                (failedToLoad ? (
                  <Button variant="outline" onClick={() => void refetch()}>
                    Try again
                  </Button>
                ) : (
                  <Button>{existing ? 'Review' : 'Add'}</Button>
                ))
              }
            />
          </CardContent>
        </Card>
      </div>
      {isOpen && orgId && !isLoading && !failedToLoad && (
        <EditEquipmentModal
          item={truck}
          isNew={!existing}
          existingItems={trucks.filter((other) => other.id !== truck.id)}
          onClose={() => setIsOpen(false)}
          onSubmit={(entered) => save(entered as Truck)}
          config={truckConfig}
          saveOnClose
        />
      )}
    </>
  )
}
